// src/component/Chat.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types'; // Keep PropTypes import
// --- Import necessary icons ---
import {
    FaImages, FaCamera, FaMicrophone, FaArrowCircleUp, FaTrash,
    FaPlusCircle, FaSpinner, FaExclamationTriangle, FaTimes, FaFileUpload, FaPaperclip
} from 'react-icons/fa';
import { toast } from 'react-toastify'; // Keep toast import
import { ChatContext } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import imageCompression from 'browser-image-compression';
import Cart from './Cart';
import apiClient from '../api/apiClient';

// --- Constants ---
const DEFAULT_PROMPTS = [
    'can you check my orders?',
    'What is in my cart?',
    'what services can I order here',
    'what services do you offer?',
];
const MAX_ORIGINAL_IMAGE_SIZE = 15 * 1024 * 1024;
const MAX_FILES_CHAT = 5;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_PDF_TYPE = 'application/pdf';
const ALLOWED_TYPES_STRING_ALL = 'image/jpeg,image/png,image/webp,image/gif,application/pdf';
const COMPRESSION_OPTIONS = {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
};
// --- Constants for backend action types (matching orchestrator) ---
const SHOW_DOC_UPLOAD_ACTION = 'SHOW_UPLOAD_DOCUMENTS_BUTTON';
const UPDATE_CART_UI_ACTION = 'UPDATE_CART_UI'; // Added constant

// --- Helper Functions --- (Unchanged)
const readFileAndGetDataUrl = (file) => {
    return new Promise((resolve, reject) => {
        if (!file || typeof file.size === 'undefined') { return reject(new Error("Invalid file object provided.")); }
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const dataUrl = reader.result;
                if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
                    if (file.type === ALLOWED_PDF_TYPE) { resolve(null); } // Null for PDF preview
                    else { throw new Error("Invalid Data URL generated."); }
                } else { resolve(dataUrl); }
            } catch (error) { reject(new Error(`Processing error: ${error.message}`)); }
        };
        reader.onerror = (error) => { reject(new Error(`FileReader error: ${error.message || 'Unknown read error'}`)); };
        reader.readAsDataURL(file);
    });
};
const compressAndGetDataUrl = async (file) => {
    const fileType = file.type || '';
    if (fileType === ALLOWED_PDF_TYPE) {
        try { return { compressedFile: file, dataUrl: null }; } // No compression, null preview for PDF
        catch (readError) { throw new Error(`Read failed for PDF ${file.name}: ${readError.message || 'Unknown read error'}`); }
    }
    const isValidTypeForCompression = ALLOWED_IMAGE_TYPES.some(allowedType => fileType === allowedType);
    if (!isValidTypeForCompression) {
        console.warn(`File type ${fileType} cannot be compressed as image, returning original.`);
        try {
            const dataUrl = await readFileAndGetDataUrl(file); // Still try to read for preview
            return { compressedFile: file, dataUrl };
        } catch (readError) { throw new Error(`Read failed for ${file.name}: ${readError.message || 'Unknown read error'}`); }
    }
    try {
        console.log(`Compressing image: ${file.name}`);
        const compressedFile = await imageCompression(file, COMPRESSION_OPTIONS);
        console.log(`Finished compressing ${file.name}. Original: ${(file.size / 1024).toFixed(1)}KB, Compressed: ${(compressedFile.size / 1024).toFixed(1)}KB`);
        const dataUrl = await readFileAndGetDataUrl(compressedFile);
        return { compressedFile, dataUrl };
    } catch (error) { throw new Error(`Processing failed for ${file.name}: ${error.message || 'Unknown error during compression/read'}`); }
};


// --- Component ---
const Chat = () => {
    const { user: authUser } = useAuth();
    // *** Get triggerDocumentUpload from context ***
    const {
        chatState, setChatState, startNewSession, showInlineCart, setShowInlineCart,
        triggerDocumentUpload // Get the function from context
    } = useContext(ChatContext);

    // --- State Variables ---
    const [inputText, setInputText] = useState('');
    const [showAudioModal, setShowAudioModal] = useState(false);
    const [isSending, setIsSending] = useState(false); // Sending chat message
    const [attachedFiles, setAttachedFiles] = useState([]); // Files for CHAT message preview ONLY
    const [isAttaching, setIsAttaching] = useState(false); // Processing CHAT files ONLY
    const [attachmentError, setAttachmentError] = useState(null); // Error related to CHAT file selection/processing
    const [showAttachmentChoice, setShowAttachmentChoice] = useState(false);

    // --- State for displaying the upload button ---
    const [uploadButtonInfo, setUploadButtonInfo] = useState(null); // Stores { orderItemId, label }

    // --- REMOVED Order Upload State ---

    // --- Refs ---
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const isSendingRef = useRef(false); // Double-send prevention

    // --- Effects ---
    const scrollToBottom = useCallback(() => { setTimeout(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, 50); }, []);
    useEffect(() => { scrollToBottom(); }, [chatState.messages, isSending, showInlineCart, scrollToBottom]); // Simplified dependencies
    useEffect(() => { const ta = textareaRef.current; if (ta) { ta.style.height = 'auto'; const scrollHeight = ta.scrollHeight; ta.style.height = `${scrollHeight}px`; } }, [inputText]);

    // Effect to reset state on session change
     useEffect(() => {
         setInputText('');
         setAttachedFiles([]);
         setAttachmentError(null);
         setShowAttachmentChoice(false);
         setUploadButtonInfo(null); // <-- Reset button state
         setShowInlineCart(false); // <-- Reset cart state
         if (fileInputRef.current) fileInputRef.current.value = null;
         setIsSending(false);
         setIsAttaching(false);
         isSendingRef.current = false; // Reset double-send ref
     }, [chatState.activeSessionId]); // Added setShowInlineCart


    // --- Utility Functions ---
    const addSystemMessage = useCallback((content, isError = false) => {
        const systemMessageId = `system-${crypto.randomUUID()}`;
        console.log(`[addSystemMessage] ID: ${systemMessageId}, Error: ${isError}, Content:`, content.substring(0, 100));
        const systemMessage = { id: systemMessageId, sender: 'system', content: content, timestamp: new Date().toISOString(), isError: isError, isLoading: false };
        setChatState(prev => ({ ...prev, messages: [...prev.messages, systemMessage] }));
        scrollToBottom();
    }, [setChatState, scrollToBottom]);

    const updateMessageState = useCallback((messageId, content, isError = false) => {
        const displayContent = typeof content === 'string' ? content : JSON.stringify(content);
        console.log(`[updateMessageState] Attempting update for ID: ${messageId}`, { isError, contentPreview: displayContent.substring(0, 100) });
        setChatState(prev => {
            let found = false;
            const newMessages = prev.messages.map(msg => {
                if (msg.id === messageId) {
                    found = true;
                    if (msg.content !== displayContent || msg.isLoading !== false || msg.isError !== isError) {
                         console.log(`[updateMessageState] Updating message ${messageId}.`);
                         return { ...msg, content: displayContent, isLoading: false, isError };
                    } else {
                         console.log(`[updateMessageState] Message ${messageId} already has target state.`);
                         return msg;
                    }
                }
                return msg;
            });
            if (!found) { console.warn(`[updateMessageState] Message ID ${messageId} not found.`); return prev; }
             console.log(`[updateMessageState] Finished processing state update for ID ${messageId}.`);
            return { ...prev, messages: newMessages };
        });
    }, [setChatState]);

    const clearAttachments = useCallback(() => {
        setAttachedFiles([]);
        setAttachmentError(null);
        if (fileInputRef.current) { fileInputRef.current.value = null; }
    }, []);

    // --- REMOVED uploadOrderDocument function ---
    // --- REMOVED handleChatResponse function ---

    // --- Modified sendMessage to handle actions_required ---
    const sendMessage = useCallback(async () => {
        if (isSendingRef.current || isSending || isAttaching || !chatState.activeSessionId || chatState.isLoadingSession) {
             console.warn("SendMessage prevented:", { isSendingRef: isSendingRef.current, isSending, isAttaching, activeSessionId: chatState.activeSessionId, isLoadingSession: chatState.isLoadingSession });
             return;
        }
        isSendingRef.current = true; // Prevent double send

        // Clear previous action-driven UI elements before sending
        if (uploadButtonInfo) setUploadButtonInfo(null);
        if (showInlineCart) setShowInlineCart(false); // Close cart before new interaction potentially shows it again

        const messageToSend = inputText.trim();
        const currentSessionId = chatState.activeSessionId;
        const filesToAttach = [...attachedFiles]; // Use the state for chat attachments
        const includeAttachments = filesToAttach.length > 0;

        if (!messageToSend && !includeAttachments) {
            isSendingRef.current = false;
            return;
        }

        setIsSending(true);
        setAttachmentError(null);

        const userMessageId = `user-${crypto.randomUUID()}`;
        const naokoPlaceholderId = `naoko-${crypto.randomUUID()}`;
        const attachmentPreviews = includeAttachments ? filesToAttach.map(f => ({ id: f.id, dataUrl: f.dataUrl, name: f.file?.name, isPdf: f.file?.type === ALLOWED_PDF_TYPE })) : [];
        const userMessage = { id: userMessageId, sender: 'user', content: messageToSend, attachmentsPreview: attachmentPreviews, timestamp: new Date().toISOString() };
        const naokoPlaceholder = { id: naokoPlaceholderId, sender: 'naoko', content: '...', timestamp: new Date().toISOString(), isLoading: true };

        console.log(`[sendMessage] Adding User Msg ID: ${userMessageId}, Placeholder ID: ${naokoPlaceholderId}`);

        setChatState(prev => ({ ...prev, messages: [...prev.messages, userMessage, naokoPlaceholder] }));
        setInputText('');
        clearAttachments(); // Clear CHAT attachments preview

        const formData = new FormData();
        formData.append('session_id', currentSessionId);
        formData.append('message', messageToSend);
        filesToAttach.forEach((att) => { if (att.file instanceof Blob || att.file instanceof File) { formData.append('attachments', att.file, att.file.name || `attachment-${att.id}`); } });

        try {
            const response = await apiClient.post('/chat', formData);
            const responseData = response.data;
            console.log("<<< RECEIVED API RESPONSE (sendMessage) >>>", responseData); // Log the actual object

            // --- Get the user-friendly reply (assuming backend sends it correctly) ---
            let finalReply = responseData?.reply;
            if (typeof finalReply !== 'string' || finalReply.trim() === '') {
                // Provide a generic fallback reply if backend doesn't send one
                finalReply = "Okay, done.";
                console.warn("[sendMessage] Received empty or non-string reply from backend. Using fallback.");
            }

            // --- Update the AI message bubble with the user-friendly text ---
            console.log(`[sendMessage Success] Updating placeholder ${naokoPlaceholderId} with reply: "${finalReply}"`);
            updateMessageState(naokoPlaceholderId, finalReply, false);

            // --- Check for Actions (Button Trigger AND/OR Cart Update) ---
            let foundRelevantAction = false; // Track if we found *any* action we care about

            // Reset action-driven UI state before checking the response
            setUploadButtonInfo(null);
            setShowInlineCart(false);

            if (responseData.actions_required && Array.isArray(responseData.actions_required)) {
                const actions = responseData.actions_required;
                console.log("[Chat.js] Processing actions_required from responseData:", actions);

                // *** Check for Cart Update Action ***
                const shouldUpdateCartUI = actions.some(action => action?.action === UPDATE_CART_UI_ACTION);
                if (shouldUpdateCartUI) {
                    console.log("[Chat.js] Action: UPDATE_CART_UI detected. Setting showInlineCart = true.");
                    setShowInlineCart(true); // <<< THIS TRIGGERS THE CART DISPLAY
                    foundRelevantAction = true;
                }

                // --- Check for Document Upload Button Action ---
                const docUploadButtonAction = actions.find(action => action?.action === SHOW_DOC_UPLOAD_ACTION);
                if (docUploadButtonAction?.context?.orderItemId) {
                     console.log("[Chat.js] Action: SHOW_UPLOAD_DOCUMENTS_BUTTON detected. Context:", docUploadButtonAction.context);
                     const buttonContext = docUploadButtonAction.context;
                     const newButtonInfo = {
                         orderItemId: buttonContext.orderItemId,
                         label: buttonContext.buttonLabel || `Upload Documents for Item ${buttonContext.orderItemId}`
                     };
                     setUploadButtonInfo(newButtonInfo); // <<< THIS TRIGGERS THE UPLOAD BUTTON
                     console.log('[Chat.js] Set uploadButtonInfo state:', newButtonInfo);
                     setAttachmentError(null); // Clear chat errors if button is shown
                     foundRelevantAction = true;
                     // Ensure cart isn't shown if upload button is the primary action (unless explicitly sent)
                     if (!shouldUpdateCartUI) setShowInlineCart(false);
                }

                 // Log if actions were present but none matched
                 if (actions.length > 0 && !foundRelevantAction) {
                     console.log("[Chat.js] actions_required contained actions, but none matched UPDATE_CART_UI or SHOW_UPLOAD_DOCUMENTS_BUTTON.");
                 }

            } else {
                // No actions array found, ensure UI elements are off
                 console.log("[Chat.js] No 'actions_required' array found in the response.");
                 // No need to explicitly set here as they were reset before the check
            }
            // --- End Action Check ---

        } catch (error) {
             const errorData = error.response?.data;
             const status = error.response?.status;
             const errorMsg = errorData?.error || errorData?.detail || error.message || 'Failed to process response';
             console.error("[Chat.js] Error sending message:", { status, errorMsg, errorData, originalError: error });
             console.error("[sendMessage Catch] Updating placeholder:", naokoPlaceholderId, "with error:", errorMsg);

             // Update placeholder with error message
             if (!error.handledByInterceptor) {
                 updateMessageState(naokoPlaceholderId, `Error: ${errorMsg} (Status: ${status || 'N/A'})`, true);
             } else {
                 updateMessageState(naokoPlaceholderId, `Error: Authentication issue`, true);
             }

             // Ensure action-driven UI is off on error
             setShowInlineCart(false);
             setUploadButtonInfo(null);

        } finally {
            setIsSending(false);
            isSendingRef.current = false; // Reset double-send ref
        }
    }, [ // Dependencies - Added showInlineCart
        inputText, chatState.activeSessionId, chatState.isLoadingSession, attachedFiles, isSending, isAttaching,
        setChatState, updateMessageState, clearAttachments, setShowInlineCart, showInlineCart, // Added showInlineCart here too
        apiClient, addSystemMessage,
        uploadButtonInfo // Include uploadButtonInfo to clear it
    ]);


    // --- Key Press Handler (Disable Enter if button showing) ---
    const handleKeyPress = useCallback((e) => {
        // Check !uploadButtonInfo and !isSendingRef.current
        if (e.key === 'Enter' && !e.shiftKey && !isSendingRef.current && !isSending && !isAttaching && !uploadButtonInfo && chatState.activeSessionId && !chatState.isLoadingSession) {
            e.preventDefault();
            sendMessage();
        }
    }, [sendMessage, isSending, isAttaching, chatState.activeSessionId, chatState.isLoadingSession, uploadButtonInfo]); // Add uploadButtonInfo

    // --- New Chat Handler (Add cleanup for button and cart) ---
    const handleNewChat = useCallback(() => {
        // Added isSendingRef check
        if (isSendingRef.current || chatState.isLoadingSession || isSending || isAttaching) return;
        setUploadButtonInfo(null); // Clear button state
        setShowInlineCart(false); // Clear cart state
        startNewSession();
    }, [startNewSession, chatState.isLoadingSession, isSending, isAttaching, setShowInlineCart]); // Added setShowInlineCart


    // --- Attach Click Handler (Simplify - always for chat now) ---
    const handleAttachClick = useCallback(() => {
        if (isAttaching || isSending || isSendingRef.current || chatState.isLoadingSession || !chatState.activeSessionId) return;
        // If button is showing, disable attaching for chat
        if (uploadButtonInfo) {
            toast.info("Please click the 'Upload Documents' button below to proceed.");
            return;
        }
        if (attachedFiles.length >= MAX_FILES_CHAT) {
             setAttachmentError(`Cannot attach more than ${MAX_FILES_CHAT} files for chat.`);
             return;
        }
        setAttachmentError(null);
        setShowAttachmentChoice(true); // Show choice modal for chat attachments
    }, [isAttaching, isSending, isSendingRef, chatState.isLoadingSession, chatState.activeSessionId, attachedFiles.length, MAX_FILES_CHAT, uploadButtonInfo]); // Added isSendingRef


    // --- File Input Trigger (Unchanged) ---
    const triggerFileInput = useCallback((useCamera = false) => { setShowAttachmentChoice(false); const input = fileInputRef.current; if (input) { if (useCamera) { input.setAttribute('capture', 'environment'); } else { input.removeAttribute('capture'); } input.value = null; input.click(); } }, []);
    const handleChooseGallery = useCallback(() => { triggerFileInput(false); }, [triggerFileInput]);
    const handleTakePhoto = useCallback(() => { triggerFileInput(true); }, [triggerFileInput]);


    // --- handleFileChange (Simplified - only for chat attachments) ---
    const handleFileChange = useCallback(async (event) => {
        if (fileInputRef.current) { fileInputRef.current.removeAttribute('capture'); }
        const files = event.target.files;
        if (!files || files.length === 0) return;

        // This function now ONLY handles CHAT attachments
        console.log("[Chat.js] handleFileChange processing for regular CHAT message.");
        if (attachedFiles.length + files.length > MAX_FILES_CHAT) {
            setAttachmentError(`Cannot attach more than ${MAX_FILES_CHAT} files.`);
            if (event.target) event.target.value = null; return;
        }
        setIsAttaching(true); setAttachmentError(null);
        let currentErrors = []; let newFilesToAdd = [];

        for (const file of files) {
            const fileType = file.type || '';
            const isAllowedType = fileType === ALLOWED_PDF_TYPE || ALLOWED_IMAGE_TYPES.some(allowedImageType => fileType === allowedImageType);
            if (!isAllowedType) { currentErrors.push(`${file.name}: Invalid type (Image/PDF)`); continue; }
            if (file.size > MAX_ORIGINAL_IMAGE_SIZE) { currentErrors.push(`${file.name}: Too large`); continue; }
            try {
                const { compressedFile, dataUrl } = await compressAndGetDataUrl(file);
                const finalFile = new File([compressedFile], file.name, { type: compressedFile.type, lastModified: Date.now() });
                newFilesToAdd.push({ id: `file-${crypto.randomUUID()}`, file: finalFile, dataUrl: dataUrl });
            } catch (error) {
                console.error(`Error processing file ${file.name} for chat:`, error);
                currentErrors.push(`${file.name}: ${error.message}`);
            }
        }
        if (newFilesToAdd.length > 0) { setAttachedFiles(prev => [...prev, ...newFilesToAdd].slice(0, MAX_FILES_CHAT)); }
        if (currentErrors.length > 0) { setAttachmentError(`Could not attach: ${currentErrors[0]}${currentErrors.length > 1 ? ` (+${currentErrors.length - 1} others)` : ''}`); }
        setIsAttaching(false);
        if (event.target) event.target.value = null;

    }, [attachedFiles.length, MAX_FILES_CHAT]); // Dependencies simplified


    // --- Remove CHAT Attachment Preview ---
    const removeAttachment = useCallback((idToRemove) => {
        setAttachedFiles(prev => prev.filter(file => file.id !== idToRemove));
        // Clear max file error if applicable
        if (attachmentError?.includes('Maximum') && attachedFiles.length - 1 < MAX_FILES_CHAT) {
            setAttachmentError(null);
        }
    }, [attachedFiles.length, attachmentError, MAX_FILES_CHAT]); // Dependencies updated

    // --- Click handler for the dynamically rendered Upload Documents button ---
    const handleUploadButtonClick = useCallback(() => {
        if (uploadButtonInfo?.orderItemId && triggerDocumentUpload) {
            console.log(`[Chat.js] Upload button clicked for item: ${uploadButtonInfo.orderItemId}`);
            triggerDocumentUpload(uploadButtonInfo.orderItemId); // Call context function to open modal
            setUploadButtonInfo(null); // Hide the button after clicking
        } else {
            console.error("Cannot trigger upload: Missing button info or context function.", { uploadButtonInfo, triggerDocumentUpload });
            toast.error("Upload functionality is currently unavailable.");
        }
    }, [uploadButtonInfo, triggerDocumentUpload]); // Dependencies

    // --- Derived State / Conditions (Updated) ---
    const isProcessing = isSending || isAttaching; // Simplified
    const isInteractionDisabled = isProcessing || isSendingRef.current || !chatState.activeSessionId || chatState.isLoadingSession;
    // Can send if text/files present AND not processing AND no button showing
    const canSendMessage = (inputText.trim().length > 0 || attachedFiles.length > 0) && !isInteractionDisabled && !uploadButtonInfo;
    // Can click attach if not processing AND no button showing AND below chat limit
    const canClickAttach = !isInteractionDisabled && !uploadButtonInfo && attachedFiles.length < MAX_FILES_CHAT;

    // --- Render Logic ---

    if (chatState.isLoadingSession && chatState.activeSessionId === null && chatState.sessionId === null && !chatState.sessionError) {
        return <div className="loading-overlay">Initializing Chat...<FaSpinner className="spin"/></div>;
    }

    return (
        <div className="chat-container">
            <div className="chat-area">
                {/* Header */}
                <div className="chat-header">
                    <h3>Naoko Assistant {chatState.activeSessionId ? `(Session ${chatState.activeSessionId})` : ''}</h3>
                     {chatState.sessionError && !chatState.isLoadingSession && chatState.sessionId !== null && ( <span className="session-error-text" title={chatState.sessionError}> <FaExclamationTriangle style={{ marginRight: '4px', verticalAlign: 'middle' }}/> Load Error </span> )}
                </div>

                {/* Main Chat Content Area */}
                <div className="chat-content-area">
                     {/* New Chat Button */}
                     <div className="new-chat-button-container">
                         <button onClick={handleNewChat} className="new-chat-button-alt" title="Start New Chat" disabled={isInteractionDisabled} > <FaPlusCircle /> New Chat {chatState.isLoadingSession && chatState.activeSessionId === null && chatState.sessionId === null && <FaSpinner className="spin" style={{marginLeft: '8px'}}/> } </button>
                    </div>

                    {/* --- Conditional Rendering --- */}
                    {!chatState.isLoadingSession && chatState.sessionError && chatState.activeSessionId === null ? ( <div className="default-state centered-message error-background"> {/* Error loading session list */} <h4>Error Loading Chat</h4> <p>{chatState.sessionError}</p> <button onClick={handleNewChat} className="inline-link-button" disabled={chatState.isLoadingSession}> Try Starting a New Chat </button> </div> )
                    : !chatState.activeSessionId && !chatState.isLoadingSession ? ( <div className="default-state centered-message"> {/* No active session selected */} <h4>Select a session</h4> <p>Choose a chat session from the side panel or start a new one.</p> <button onClick={handleNewChat} className="new-chat-button-alt" disabled={chatState.isLoadingSession}> <FaPlusCircle /> Start New Chat </button> </div> )
                    : chatState.activeSessionId && chatState.messages.length === 0 && !isProcessing && !uploadButtonInfo && attachedFiles.length === 0 ? (
                         // Empty Active Session
                         <div className="default-state">
                             <p className="greeting">Hi{authUser?.fullName ? ` ${authUser.fullName.split(' ')[0]}` : ''}, I’m Naoko. How can I assist?</p>
                              {/* Input area in middle for empty state */}
                              <div className="chat-input-container chat-input-middle">
                                 <textarea ref={textareaRef} value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message or query..." rows="1" disabled={isInteractionDisabled} aria-label="Chat message input" />
                                 <div className="input-buttons">
                                     <button onClick={handleAttachClick} title="Attach Files" disabled={!canClickAttach} className={attachedFiles.length > 0 ? 'has-images' : ''}> <FaPaperclip /> {attachedFiles.length > 0 && <span className="image-badge">{attachedFiles.length}</span>} </button>
                                     <button onClick={() => setShowAudioModal(true)} title="Record Audio (Coming Soon)" disabled={true}><FaMicrophone /></button>
                                     <button onClick={sendMessage} title="Send Message" disabled={!canSendMessage} className="send-button">{isSending ? <FaSpinner className="spin"/> : <FaArrowCircleUp />}</button>
                                 </div>
                              </div>
                              <div className="prompts"> {DEFAULT_PROMPTS.map(p => <div key={p} className="prompt-card" onClick={() => { setInputText(p); setTimeout(() => sendMessage(), 50); }}>{p}</div>)} </div>
                              {isAttaching && <p className="attaching-indicator"><FaSpinner className="spin"/> Processing files...</p>}
                              {attachmentError && <p className="attachment-error">{attachmentError}</p>}
                         </div>
                    ) : chatState.activeSessionId ? (
                         // Active Session with Messages OR Upload Button Showing
                         <>
                             {/* Messages Area */}
                             <div className="messages">
                                 {chatState.messages.map((msg) => {
                                      if (!msg.id) { console.error("Rendering message without ID!", msg); msg.id = `missing-id-${crypto.randomUUID()}`; }
                                     return (
                                         <div key={msg.id} className={`message ${msg.sender} ${msg.isLoading ? 'loading' : ''} ${msg.isError ? 'error' : ''} ${msg.sender === 'system' ? 'system' : ''}`}>
                                             {msg.isError ? ( <p><i>{msg.content || 'Failed to get response.'}</i></p> ) : ( msg.content && <p>{msg.content}</p> )}
                                             {msg.attachmentsPreview && msg.attachmentsPreview.length > 0 && ( <div className="message-attachment-previews"> {msg.attachmentsPreview.map(preview => ( preview.dataUrl ? <img key={preview.id} src={preview.dataUrl} alt={preview.name || 'Attachment'} className="message-attachment-thumbnail" title={preview.name || 'Attachment'}/> : <span key={preview.id} className="file-icon message-attachment-placeholder" title={preview.name || 'Attachment'}>📄</span> ))} </div> )}
                                             {!msg.isLoading && msg.timestamp && !isNaN(new Date(msg.timestamp).getTime()) && ( <span className="timestamp">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span> )}
                                             {msg.isLoading && msg.sender === 'naoko' && ( <FaSpinner className='spin typing-indicator' /> )}
                                         </div>
                                     );
                                 })}
                                 <div style={{ height: '1px' }} ref={messagesEndRef} /> {/* Scroll target */}
                             </div>

                             {/* Inline Cart */}
                             {showInlineCart && ( <div className="inline-cart-container"> <Cart isInline={true} /> <button onClick={() => setShowInlineCart(false)} className="inline-cart-close-btn" title="Close Cart View"><FaTimes /></button> </div> )}

                             {/* --- Render Upload Button Area --- */}
                             {uploadButtonInfo && (
                                <div className="upload-button-prompt-area">
                                     <button onClick={handleUploadButtonClick} className="upload-document-button">
                                         <FaFileUpload style={{ marginRight: '8px' }} />
                                         {uploadButtonInfo.label || 'Upload Required Documents'}
                                     </button>
                                     <p className="upload-button-note">Click the button above to open the document uploader.</p>
                                 </div>
                             )}

                             {/* Chat Attachments Preview Area (Only show if NO button is active) */}
                             {!uploadButtonInfo && (attachedFiles.length > 0 || isAttaching || attachmentError) && (
                                 <div className="attachments-preview-area">
                                      {attachedFiles.length > 0 && <h5>Chat Attachments ({attachedFiles.length}/{MAX_FILES_CHAT}):</h5>}
                                      {isAttaching && <div className='attaching-indicator'><FaSpinner className="spin"/> Processing...</div>}
                                      {attachmentError && !isAttaching && <p className="attachment-error inline-error">{attachmentError}</p>}
                                      {attachedFiles.length > 0 && (
                                         <ul className="attachments-list">
                                              {attachedFiles.map((att) => (
                                                  <li key={att.id}>
                                                      {att.dataUrl && <img src={att.dataUrl} alt={att.file?.name || 'attachment'} className="attachment-thumbnail"/>}
                                                      {!att.dataUrl && <span className="file-icon" title={att.file?.name}>📄</span>}
                                                      <span className="attachment-name" title={att.file?.name}>{att.file?.name || 'Attached File'}</span>
                                                      <span className="attachment-size">({(att.file?.size / 1024).toFixed(0)} KB)</span>
                                                      <button onClick={() => removeAttachment(att.id)} className="remove-attachment-btn" title="Remove Attachment" disabled={isAttaching || isSending || isSendingRef.current}><FaTrash /></button>
                                                  </li>
                                              ))}
                                         </ul>
                                      )}
                                 </div>
                             )}


                             {/* Chat Input Area (Adjusted for button state) */}
                             <div className="chat-input-container">
                                 <textarea
                                      ref={textareaRef}
                                      value={inputText}
                                      onChange={(e) => setInputText(e.target.value)}
                                      onKeyPress={handleKeyPress}
                                      placeholder={
                                          uploadButtonInfo ? "Click the 'Upload Documents' button above." // Show instruction when button active
                                          : isInteractionDisabled ? "Session loading or processing..."
                                          : "Type your message..."
                                      }
                                      rows="1"
                                      // Disable text input completely when the upload button is showing
                                      disabled={isInteractionDisabled || !!uploadButtonInfo}
                                      aria-label="Chat message input"
                                 />
                                 <div className="input-buttons">
                                      {/* Attach Button: Disabled if button showing or chat limit reached */}
                                      <button
                                           onClick={handleAttachClick}
                                           title={"Attach Files to Chat"}
                                           disabled={!canClickAttach} // Simplified condition
                                           className={attachedFiles.length > 0 ? 'has-images' : ''}
                                      >
                                          <FaPaperclip />
                                          {attachedFiles.length > 0 && <span className="image-badge">{attachedFiles.length}</span>}
                                      </button>
                                     <button onClick={() => setShowAudioModal(true)} title="Record Audio (Coming Soon)" disabled={true}><FaMicrophone /></button>
                                     {/* Send Button: Disabled if interaction disabled OR upload button showing */}
                                     <button
                                          onClick={sendMessage}
                                          title="Send Message"
                                          disabled={!canSendMessage} // Updated condition checks uploadButtonInfo
                                          className="send-button"
                                     >
                                         {isSending ? <FaSpinner className="spin"/> : <FaArrowCircleUp />}
                                     </button>
                                 </div>
                             </div>
                         </>
                    ) : null }
                </div> {/* End chat-content-area */}
            </div> {/* End chat-area */}

            {/* Modals (Attachment Choice Modal simplified) */}
            {showAudioModal && ( <div className="modal"> <div className="modal-content"> <h3>Record Audio</h3> <p>This feature is coming soon!</p> <button onClick={() => setShowAudioModal(false)}>Close</button> </div> </div> )}
            {showAttachmentChoice && (
                <div className="modal-overlay" onClick={() => setShowAttachmentChoice(false)}>
                     <div className="modal-content attachment-choice-modal" onClick={(e) => e.stopPropagation()}>
                         <h4>Add File to Chat</h4>
                         <p>Choose a source:</p>
                         <button onClick={handleChooseGallery} disabled={isProcessing}> <FaImages /> Gallery / Files </button>
                         <button onClick={handleTakePhoto} disabled={isProcessing}> <FaCamera /> Take Photo </button>
                         <button onClick={() => setShowAttachmentChoice(false)} className="cancel-button" disabled={isProcessing}> Cancel </button>
                         {(isProcessing) && <p className="attaching-indicator modal-indicator"><FaSpinner className="spin"/> Processing...</p>}
                     </div>
                 </div>
            )}

            {/* Hidden File Input */}
             <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept={ALLOWED_TYPES_STRING_ALL} // Use combined accept string
                multiple={true} // Only for chat attachments now
                onChange={handleFileChange}
                disabled={isInteractionDisabled} // Use combined disable flag
             />

            {/* Styles */}
            <style jsx>{`
                /* --- Paste ALL your existing styles here --- */
                /* --- Style definitions from the previous example --- */
                /* --- (They should be complete as provided before) --- */

                /* Order Upload Prompt Styles */
                .upload-button-prompt-area { padding: 12px 20px; background-color: #e7f3ff; border-top: 1px solid #b3d7ff; border-bottom: 1px solid #b3d7ff; margin: 0 0 10px 0; flex-shrink: 0; text-align: center; }
                .upload-document-button { background-color: #007bff; color: white; border: none; padding: 10px 20px; border-radius: 25px; cursor: pointer; font-size: 0.95rem; font-weight: 600; display: inline-flex; align-items: center; gap: 8px; transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out; box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2); }
                .upload-document-button:hover:not(:disabled) { background-color: #0056b3; box-shadow: 0 3px 6px rgba(0, 86, 179, 0.3); }
                .upload-button-note { margin: 8px 0 0 0; color: #1c7ed6; font-size: 0.85rem; }

                /* Base Layout */
                .chat-container { display: flex; height: 100%; width: 100%; position: relative; overflow: hidden; background-color: #f0f2f5; }
                .chat-area { flex: 1; display: flex; flex-direction: column; height: 100%; box-sizing: border-box; overflow: hidden; }
                .chat-content-area { flex: 1; display: flex; flex-direction: column; overflow: hidden; position: relative; }
                .chat-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 20px; border-bottom: 1px solid #e0e4ec; background-color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.04); flex-shrink: 0; position: sticky; top: 0; z-index: 10; }
                .chat-header h3 { margin: 0; color: #1a237e; font-size: 1.1rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-grow: 1; }
                .session-error-text { display: inline-flex; align-items: center; color: #c62828; font-size: 0.85rem; font-weight: 500; margin: 0 15px; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; max-width: 150px; flex-shrink: 1; }

                /* New Chat Button */
                .new-chat-button-container { padding: 10px 20px; text-align: right; border-bottom: 1px solid #e8eaf6; background-color: #f8f9fa; flex-shrink: 0; }
                .new-chat-button-alt { background: none; border: 1px solid #adb5bd; color: #495057; padding: 6px 12px; border-radius: 15px; cursor: pointer; font-size: 0.85rem; display: inline-flex; align-items: center; gap: 5px; transition: all 0.2s ease; font-weight: 500; }
                .new-chat-button-alt:hover:not(:disabled) { background-color: #e9ecef; border-color: #495057; color: #212529; }
                .new-chat-button-alt:disabled { opacity: 0.6; cursor: not-allowed; }
                .new-chat-button-alt .spin { width: 0.8em; height: 0.8em; animation: spin 1.5s linear infinite; }

                /* Messages Area */
                .messages { flex-grow: 1; overflow-y: auto; padding: 15px 20px 10px 20px; scroll-behavior: smooth; }

                /* Message Bubbles */
                .message { margin-bottom: 14px; padding: 10px 15px; border-radius: 20px; max-width: 75%; width: fit-content; word-wrap: break-word; display: flex; flex-direction: column; position: relative; box-shadow: 0 1px 2px rgba(0,0,0,0.1); line-height: 1.45; }
                .message p { margin: 0; padding: 0; white-space: pre-wrap; }
                .message .timestamp { font-size: 0.7rem; opacity: 0.7; margin-top: 5px; align-self: flex-end; color: inherit; white-space: nowrap; }
                .message.user { background: #e3f2fd; color: #1976d2; margin-left: auto; border-bottom-right-radius: 8px; align-self: flex-end; }
                .message.user .timestamp { color: #42a5f5; }
                .message.naoko { background: #ffffff; color: #333; margin-right: auto; border: 1px solid #e8eaf6; border-bottom-left-radius: 8px; align-self: flex-start; }
                .message.naoko .timestamp { color: #78909c; }
                /* Loading state for AI message (placeholder) */
                .message.naoko.loading { background: #f5f7fa; color: #555; box-shadow: none; border: 1px solid #e0e4ec; }
                /* Only show the '...' content if it's actually loading */
                .message.naoko.loading p { display: none; } /* Hide text content if loading */
                .message.error { border: 1px solid #ef9a9a; background-color: #ffebee; color: #c62828; font-weight: 500; }
                .message.error p i { color: #c62828; } /* Ensure italic error text has the right color */
                .message.error .timestamp { display: none; }
                .message.system { background-color: #f0f4f8; color: #546e7a; border: none; box-shadow: none; font-size: 0.85rem; text-align: center; margin: 8px auto; max-width: 90%; border-radius: 8px; padding: 6px 12px; font-style: italic; }
                .message.system.error { background-color: #ffebee; color: #c62828; font-style: normal; font-weight: 500; }
                .typing-indicator { font-size: 0.8em; display: inline-block; vertical-align: middle; color: #90a4ae; animation: spin 1.5s linear infinite; margin-left: 8px; }
                /* Add position relative so the spinner can be centered */
                .message.naoko.loading { position: relative; min-height: 30px; /* Ensure space for spinner */ }
                .message.naoko.loading .typing-indicator { position: absolute; left: 15px; top: 50%; transform: translateY(-50%); margin-left: 0; }
                .message-attachment-previews { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; margin-bottom: 3px; }
                .message-attachment-thumbnail { max-width: 60px; max-height: 60px; height: auto; border-radius: 5px; border: 1px solid rgba(0,0,0,0.1); object-fit: cover; cursor: pointer; transition: transform 0.2s ease-in-out; }
                .message-attachment-thumbnail:hover { transform: scale(1.05); }
                .file-icon.message-attachment-placeholder { font-size: 2rem; line-height: 1; color: #adb5bd; background-color: #e9ecef; width: 60px; height: 60px; display: inline-flex; align-items: center; justify-content: center; border-radius: 5px; border: 1px solid rgba(0,0,0,0.1); }

                /* Default/Empty States */
                .default-state { display: flex; flex-direction: column; align-items: center; flex: 1; text-align: center; padding: 20px; justify-content: center; }
                .greeting { font-size: 1.6rem; color: #333; margin-bottom: 25px; font-weight: 300; }
                .prompts { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 20px; justify-content: center; max-width: 600px; }
                .prompt-card { background: #fff; padding: 12px 18px; border-radius: 25px; cursor: pointer; transition: all 0.3s; border: 1px solid #e0e4ec; box-shadow: 0 2px 4px rgba(0,0,0,0.05); font-size: 0.9rem; text-align: left; }
                .prompt-card:hover { background: #f5f7fa; box-shadow: 0 3px 6px rgba(0,0,0,0.08); transform: translateY(-2px); }
                .centered-message { align-items: center; justify-content: center; padding: 30px; flex: 1; }
                .centered-message h4 { margin-top: 0; margin-bottom: 10px; color: #333; font-size: 1.3rem; }
                .centered-message p { font-size: 1rem; color: #555; margin-bottom: 10px; line-height: 1.5; max-width: 450px; }
                .inline-link-button { background: none; border: none; color: #1a237e; text-decoration: underline; cursor: pointer; font-size: 1rem; padding: 0; }
                .error-background { background-color: #fff3f3; border-radius: 8px; padding: 30px 40px; margin: 20px; box-shadow: 0 2px 8px rgba(200, 0, 0, 0.1); text-align: center; }
                .error-text { color: #c62828; font-weight: 500; font-size: 0.9rem; margin-top: 5px; }

                /* Attachment Preview Area */
                .attachments-preview-area { padding: 10px 20px; border-top: 1px solid #e0e4ec; background-color: #f8f9fa; flex-shrink: 0; max-height: 150px; overflow-y: auto; }
                .attachments-preview-area h5 { margin: 0 0 10px 0; font-size: 0.8rem; color: #6c757d; text-transform: uppercase; font-weight: 600; }
                .attachments-list { list-style: none; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px; }
                .attachments-list li { display: flex; align-items: center; background-color: #fff; border: 1px solid #dee2e6; border-radius: 16px; padding: 5px 8px; font-size: 0.8rem; max-width: 220px; box-shadow: 0 1px 2px rgba(0,0,0,0.05); overflow: hidden; }
                .attachment-thumbnail { width: 24px; height: 24px; object-fit: cover; border-radius: 3px; margin-right: 6px; border: 1px solid #eee; flex-shrink: 0; }
                .file-icon { font-size: 1.2rem; margin-right: 6px; color: #495057; flex-shrink: 0; }
                .attachment-name { flex-grow: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-right: 4px; color: #343a40; }
                .attachment-size { font-size: 0.7rem; color: #6c757d; white-space: nowrap; margin-right: 4px; flex-shrink: 0; }
                .remove-attachment-btn { background: none; border: none; color: #6c757d; cursor: pointer; padding: 0 2px; font-size: 0.8rem; line-height: 1; margin-left: auto; opacity: 0.7; transition: opacity 0.2s; flex-shrink: 0; }
                .remove-attachment-btn:hover { color: #dc3545; opacity: 1;}
                .remove-attachment-btn:disabled { cursor: not-allowed; opacity: 0.5; }
                .attaching-indicator { font-size: 0.8rem; color: #6c757d; margin: 8px 0; display: flex; align-items: center; justify-content: center; gap: 5px; padding: 5px 0; width: 100%; }
                .attachment-error { color: #dc3545; font-size: 0.85rem; margin: 8px auto; font-weight: 500; text-align: center; max-width: 90%;}
                .attachment-error.inline-error { margin-left: 0; margin-top: 5px; padding-left: 0; text-align: left;}

                /* Chat Input Area */
                .chat-input-container { display: flex; align-items: flex-end; padding: 8px 12px; margin: 10px 20px; background-color: #ffffff; border: 1px solid #d1d5db; border-radius: 24px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out; flex-shrink: 0; }
                .chat-input-container:focus-within { border-color: #4f46e5; box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2); }
                .chat-input-container textarea { flex: 1; border: none; outline: none; padding: 8px 5px 8px 8px; font-size: 1rem; line-height: 1.5; background: transparent; color: #1f2937; resize: none; overflow-y: auto; max-height: 110px; min-height: 24px; box-shadow: none; }
                .chat-input-container textarea::placeholder { color: #9ca3af; }
                .chat-input-container textarea:disabled { background-color: transparent; color: #9ca3af; cursor: not-allowed; }
                .chat-input-container .input-buttons { display: flex; align-items: center; gap: 4px; padding-left: 8px; }
                .chat-input-container .input-buttons button { background: none; border: none; font-size: 1.3rem; cursor: pointer; color: #6b7280; padding: 7px; display: flex; align-items: center; justify-content: center; border-radius: 50%; transition: background-color 0.2s, color 0.2s; position: relative; }
                .chat-input-container .input-buttons button:hover:not(:disabled) { background-color: #e5e7eb; color: #4f46e5; }
                .chat-input-container .input-buttons button:disabled { color: #d1d5db; cursor: not-allowed; background-color: transparent; opacity: 0.7; }
                .chat-input-container .input-buttons button.send-button { color: #4f46e5; }
                .chat-input-container .input-buttons button.send-button:hover:not(:disabled) { background-color: #e0e7ff; }
                .chat-input-container .input-buttons button.send-button:disabled { color: #9ca3af; }
                .image-badge { position: absolute; top: -2px; right: -2px; min-width: 16px; height: 16px; background-color: #3b82f6; color: white; border-radius: 8px; border: 1px solid white; font-size: 0.7rem; line-height: 15px; text-align: center; padding: 0 4px; font-weight: bold; }
                button.has-images { color: #3b82f6; }
                .input-buttons button .spin { width: 1em; height: 1em; font-size: 1em; color: inherit; animation: spin 1.5s linear infinite;}
                .attaching-spinner-overlay { display: none; }
                .chat-input-middle { width: 100%; max-width: 700px; margin: 0 auto 25px auto; }

                /* Other Styles */
                .modal { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.65); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
                .modal-content { background: #fff; padding: 25px 30px; border-radius: 10px; text-align: center; box-shadow: 0 6px 20px rgba(0,0,0,0.25); max-width: 550px; width: 100%; }
                .modal-content h3 { margin-top: 0; margin-bottom: 25px; color: #1a237e; font-weight: 600; }
                .modal-content button { margin: 10px 5px; padding: 10px 20px; border: none; border-radius: 6px; background: #1a237e; color: white; cursor: pointer; transition: background 0.3s; }
                .modal-content button:hover:not(:disabled) { background: #0d1757; }
                .loading-overlay { display: flex; align-items: center; justify-content: center; flex-direction: column; position: absolute; inset: 0; background: rgba(255,255,255,0.9); z-index: 1001; font-size: 1.3rem; color: #1a237e; font-weight: 500; gap: 15px; }
                .spin { animation: spin 1.5s linear infinite; display: inline-block; }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* Inline Cart Styles */
                .inline-cart-container { border-top: 2px solid #e0e4ec; border-bottom: 1px solid #e0e4ec; padding: 15px 20px; margin: 0 0 10px 0; background-color: #f8f9fa; max-height: 40%; overflow-y: auto; flex-shrink: 0; position: relative; box-shadow: inset 0 1px 3px rgba(0,0,0,0.05); }
                .inline-cart-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #dde2eb; }
                .inline-cart-header h4 { margin: 0; font-size: 1rem; font-weight: 600; color: #343a40; }
                .inline-cart-close-btn { background: none; border: none; color: #6c757d; font-size: 1.1rem; cursor: pointer; padding: 2px; line-height: 1; opacity: 0.8; transition: color 0.2s, opacity 0.2s; position: absolute; top: 10px; right: 15px; } /* Added positioning */
                .inline-cart-close-btn:hover { color: #dc3545; opacity: 1; }

                /* Attachment Choice Modal Styles */
                .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
                .modal-content.attachment-choice-modal { background: #fff; padding: 25px 30px; border-radius: 10px; text-align: center; box-shadow: 0 6px 20px rgba(0,0,0,0.25); max-width: 350px; width: 100%; }
                .attachment-choice-modal h4 { margin-top: 0; margin-bottom: 10px; color: #1a237e; font-weight: 600; font-size: 1.1rem; }
                .attachment-choice-modal p { margin-bottom: 20px; color: #555; font-size: 0.95rem; }
                .attachment-choice-modal button { display: flex; align-items: center; justify-content: center; width: 100%; margin: 10px 0; padding: 12px 20px; border: 1px solid #dee2e6; border-radius: 8px; background: #f8f9fa; color: #343a40; cursor: pointer; font-size: 1rem; font-weight: 500; transition: background 0.2s, border-color 0.2s, box-shadow 0.2s; }
                .attachment-choice-modal button:not(.cancel-button):not(:disabled):hover { background: #e9ecef; border-color: #adb5bd; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
                .attachment-choice-modal button svg { margin-right: 12px; font-size: 1.3em; color: #495057; }
                .attachment-choice-modal button.cancel-button { background: none; border: none; color: #6c757d; margin-top: 15px; padding: 8px; font-weight: 500; box-shadow: none; }
                .attachment-choice-modal button.cancel-button:hover:not(:disabled) { color: #343a40; background: none; border-color: transparent; }
                .attachment-choice-modal button:disabled { opacity: 0.6; cursor: not-allowed; background-color: #f8f9fa; border-color: #dee2e6; box-shadow: none; }
                .attachment-choice-modal .modal-indicator { font-size: 0.85rem; color: #6c757d; margin-top: 15px; }

            `}</style>
        </div>
    );
};

// Add PropTypes if using them, otherwise optional
// Chat.propTypes = { /* Define any props if needed */ };

export default Chat;